import type * as THREE from 'three'
import type { Vector3Like } from 'three'

/**
 * Per-points-array cache of surface-projected curves.
 *
 * Keyed on the stored points array REFERENCE (WeakMap), so as long as the same
 * Redux-held points array is passed to a `<FreeLine>`, the projection runs at
 * most once across all renders, all orbits, and all remounts. The inner map is
 * keyed on bodyMesh reference (also WeakMap) because the same line can appear
 * on two different body meshes (dual-body view) and must be projected onto each.
 *
 * Cache entries are dropped automatically when either reference is GC'd
 * (drawing removed / body unloaded), so there is no manual lifecycle to manage
 * in production.
 */
type ComputeFn = () => THREE.Curve<THREE.Vector3>

interface Inner {
  noBody?: THREE.Curve<THREE.Vector3>
  byBody: WeakMap<object, THREE.Curve<THREE.Vector3>>
}

let outer: WeakMap<readonly Vector3Like[], Inner> = new WeakMap()

export const getOrComputeProjectedCurve = (
  points: readonly Vector3Like[],
  bodyMesh: THREE.Object3D | undefined,
  compute: ComputeFn,
): THREE.Curve<THREE.Vector3> => {
  let inner = outer.get(points)
  if (!inner) {
    inner = { byBody: new WeakMap() }
    outer.set(points, inner)
  }

  if (!bodyMesh) {
    if (inner.noBody) return inner.noBody
    const created = compute()
    inner.noBody = created
    return created
  }

  const existing = inner.byBody.get(bodyMesh)
  if (existing) return existing
  const created = compute()
  inner.byBody.set(bodyMesh, created)
  return created
}

/**
 * Test-only: reset the cache by swapping the underlying WeakMap. Production
 * code does not need to call this — WeakMap entries are reclaimed automatically
 * when their key references become unreachable.
 */
export const clearProjectedCurveCache = (): void => {
  outer = new WeakMap()
}
